import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private baseUrl: string = environment.apiUrl;

  authInitialized = new Promise(
    (resolve) => (this.authInitializedResolve = resolve)
  );
  authInitializedResolve;

  private readonly errorMap: { [key: string]: string } = {
    'auth/invalid-credential':
      'Your credentials are invalid. Please check your email and password.',
    'auth/email-already-in-use':
      'Email already in use. Try signing in or resetting the password.',
  };

  private errorMessageMap: { [errorCode: string]: string } = {
    'auth/account-exists-with-different-credential':
      'This email is already associated with a different sign-in method. Please use that method or reset your password.',
  };

  constructor(
    private snackBar: MatSnackBar,
    private sessionService: SessionService,
    private http: HttpClient
  ) {}

  async handleLogin(accessToken: any, brandName: string, purpose: string) {
    try {
      const responseFromServerLogin = await this.login(
        accessToken,
        brandName,
        purpose
      );
      this.sessionService.saveSession(responseFromServerLogin);
      return this.sessionService.session || responseFromServerLogin;
    } catch (error) {
      console.error('Login error:', error);
      if (error.status === 500) {
        this.handleError(error.error, this.snackBar);
      }
      throw error;
    }
  }

  logout = async () => {
    await this.revokeServerSession();
    this.sessionService.clearSessionAndLogout();
  };

  /** Best-effort server-side session revoke (audit: auth.logout). */
  async revokeServerSession(): Promise<void> {
    const token = this.sessionService.session?.token;
    if (!token) return;
    try {
      const url = new URL('user/logout', this.baseUrl).toString();
      await this.http
        .post(url, {}, { headers: { Authorization: token } })
        .toPromise();
    } catch {
      /* the local session is cleared regardless */
    }
  }

  private async login(
    accessToken: any,
    brandName: string,
    purpose: string
  ): Promise<any> {
    const url = new URL('user/login', this.baseUrl).toString();
    return this.http
      .post(url, {
        accessToken: accessToken,
        brandName: brandName,
        purpose: purpose,
        // India rollout: forward the anonymous-session market signal (set from a
        // ?region=in inbound link) so the server can copy it onto a NEW account
        // at creation. Ignored for existing users (market is sticky, admin-only).
        market: this.getStoredMarket(),
      })
      .toPromise();
  }

  private getStoredMarket(): 'IL' | 'IN' | undefined {
    try {
      const m = localStorage.getItem('app_market');
      return m === 'IN' || m === 'IL' ? m : undefined;
    } catch {
      return undefined;
    }
  }

  isAdmin(): boolean {
    const session = this.sessionService.session;
    return session?.user?.isAdmin === true;
  }

  // Skip/dev controls for tests. F1 (Danna QA 09.09): staging is NOT a safe
  // place to leak them - it is the exact build that goes to TestFlight, so
  // '\''Skip (Dev)'\'' was visible to every tester. The gate is now an explicit
  // per-environment flag (true only in development) instead of !production,
  // and admins keep the shortcut everywhere.
  canSkipTests(): boolean {
    return environment.allowTestSkip === true || this.isAdmin();
  }

  public handleError(error: any, snackBar: MatSnackBar) {
    const code = error.code || 'unknown';
    const displayText =
      this.errorMessageMap[code] ||
      error.message ||
      'An unknown error occurred.';
    snackBar.open(displayText, 'OK', { duration: 5000 });
  }
}
